import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { getInt } from 'utils/functions';
import PropTypes from 'prop-types';
import { useMutation, useSubscription } from 'hooks/apollo';
import { REMOVE_FINANCIAL_PRODUCT_RULE } from '../../../mutations';
import { FINANCIAL_PRODUCT_RULE_REMOVED } from '../../../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { Grid, Button } from '@mui/material';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import { FinancialProductRulesListWrapper } from './FinancialProductRuleListStyle';
import FinancialProductRuleList from './FinancialProductRuleList';
import AddIcon from '@mui/icons-material/Add';
import { get } from '@totalsoft/change-tracking-react';

const FinancialProductRulesContainer = ({ canWrite, rules, isSaved, generalDataLens }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const match = useRouteMatch();
  const productId = match.params.id && getInt(match.params.id);
  const historyContext = useHistoryWithContext();

  const productName = generalDataLens ? (generalDataLens |> get)?.productName ?? '' : '';

  const [deleteFinancialProductRule] = useMutation(REMOVE_FINANCIAL_PRODUCT_RULE);

  useSubscription(FINANCIAL_PRODUCT_RULE_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Rules.FinancialProductRuleRemoved'), 'success');
      client.cache.evict({
        id: `FinancialProductRuleEditItem:{"financialProductRuleId":${data.financialProductRuleDeleted.ruleId}}`
      });
      client.cache.gc();
    }
  });

  const handleAdd = useCallback(() => {
    historyContext.push(`/financialProducts/${productId}/rules/new`, { financialProductRules: rules, productName: productName });
  }, [historyContext, productId, rules, productName]);

  const handleEdit = useCallback(
    ruleId => () => {
      historyContext.push(`/financialProducts/${productId}/rules/${ruleId}`, { productName: productName });
    },
    [historyContext, productId, productName]
  );

  const handleDelete = useCallback(
    ruleId => () => {
      if (window.confirm(t('FinancialProduct.Details.Rules.DeleteFinancialProductRule'))) {
        deleteFinancialProductRule({
          variables: {
            input: { ruleId }
          }
        });
      }
    },
    [deleteFinancialProductRule, t]
  );

  return (
    <Grid container>
      <Grid item xs={12} sm={12} ms={12} padding={2} display='flex' justifyContent='flex-end'>
        <Button color='primary' variant='contained' startIcon={<AddIcon />} disabled={!isSaved || !canWrite} onClick={handleAdd}>
          {t('Add')}
        </Button>
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <FinancialProductRulesListWrapper>
          <FinancialProductRuleList
            financialProductRules={rules}
            onEditLink={handleEdit}
            onDeleteLink={handleDelete}
            canWrite={canWrite}
            isSaved={isSaved}
          />
        </FinancialProductRulesListWrapper>
      </Grid>
    </Grid>
  );
};

FinancialProductRulesContainer.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  rules: PropTypes.array.isRequired,
  isSaved: PropTypes.bool.isRequired,
  generalDataLens: PropTypes.object.isRequired
};

export default FinancialProductRulesContainer;
